import { Customer, CustomerIdToRequestsType, CustomerRequest } from "../../Models/Customers/Customer";
import { Filter, FilterId } from "../../Models/Filter";
import { FilteringManagerNoContext, FilteringManagerWithContext } from "../../Filtering/FilteringManager";
import { FilterCheck, FilterCheckWithContext } from "../../Filtering/FilterCheck";

export interface MyCustomersFilterCheckContext {
    customerIdToRequests: CustomerIdToRequestsType | null;
}

// ===============================================================================

/**
* The filter check for a my customers home search
*/
export class MyCustomersSearchFilterCheck extends FilterCheck<Customer> {

    // ===============================================================================

    public passesCheck(object: Customer) {
        const valsToCheck = [object.companyName ?? "", ...object.customerContacts.map(c => c.name), ...object.customerContacts.map(c => c.email)].map(v => v.toLowerCase().trim());
        return valsToCheck.some(v => v.indexOf(this._activeFilter.value.toLowerCase().trim()) !== -1);
    }
}

// ===============================================================================

/**
* The filter check for a my customers home has active requests check
*/
export class MyCustomersHasActiveRequestsFilterCheck extends FilterCheckWithContext<Customer, MyCustomersFilterCheckContext> {

    // ===============================================================================

    public passesCheck(object: Customer) {
        if (this._context.customerIdToRequests === null) {
            return false;
        }

        const valToCheck = this._context.customerIdToRequests[object.customerId].some(r => r.dateResponded === null) ? "yes" : "no";
        return valToCheck === this._activeFilter.value.toLowerCase().trim();
    }
}

// ===============================================================================

/**
* Concrete class for my customers home filtering operations.
*/
export class MyCustomersFilteringManager extends FilteringManagerWithContext<Customer, MyCustomersFilterCheckContext> {
    protected filterCheckFactory(activeFilter: Filter, context: MyCustomersFilterCheckContext) {
        switch (activeFilter.filterId) {
            case FilterId.MyCustomerSearch:
                return new MyCustomersSearchFilterCheck(activeFilter);
            case FilterId.HasActiveRequests:
                return new MyCustomersHasActiveRequestsFilterCheck(activeFilter, context)
            default:
                throw new Error("Unknown Customer Filter Id: " + activeFilter.filterId);
        }
    }
}

// ===============================================================================

/**
 * The filter check for a my customers requests search.
 */
export class MyCustomersRequestsSearchFilterCheck extends FilterCheck<CustomerRequest>{

    // ===============================================================================

    public passesCheck(object: CustomerRequest) {
        const valsToCheck = [object.requestData.requestContactName.trim().toLowerCase(), object.requestData.requestContactEmail.trim().toLowerCase()];

        if (object.requestData.factoryName !== "") {
            valsToCheck.push(object.requestData.factoryName.trim().toLowerCase());
        }

        return valsToCheck.some(v => v.indexOf(this._activeFilter.value.toLowerCase().trim()) !== -1);
    }
}

// ===============================================================================

/**
* Concrete class for my customers request filtering operations.
*/
export class MyCustomersRequestsFilteringManager extends FilteringManagerNoContext<CustomerRequest> {
    protected filterCheckFactory(activeFilter: Filter) {
        switch (activeFilter.filterId) {
            case FilterId.MyCustomerRequestsSearch:
                return new MyCustomersRequestsSearchFilterCheck(activeFilter);
            default:
                throw new Error("Unknown Customer Filter Id: " + activeFilter.filterId);
        }
    }
}
